/* eslint-disable no-debugger */
import { useObserver } from "mobx-react-lite";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuftragsApi, useDocumentApi } from "../../services/ApiClient";
import LoadingSpinnerContainer from "../LoadingSpinner/LoadingSpinnerContainer";
import Modal from "./Modal";
import styles from "./Modal.module.scss";

const initialMetaData = () => ({
  group: 0,
  re_nr: "",
  dok_eingangsdat: new Date(),
  umsatz: 0,
  steuer: 0,
});

const UploadInvoiceModal = (props) => {
  const { show, onHide, data, invoicedata, dataStore, orderId, rechnungsType, invoiceIsPdf, order } = props;

  const auftragApi = useAuftragsApi();
  const documentApi = useDocumentApi();

  const [metaData, setMetaData] = useState(initialMetaData());
  const [fileUploaded, setFileUploaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isTTAenabled =
    ((process.env.REACT_APP_CLIENT === "wbg" || process.env.REACT_APP_CLIENT === "wbg_stage") &&
      (order.mandant === "ennstal" || order.mandant === "TEST_ennstal" || order.mandant === "ENW")) ||
    process.env.REACT_APP_CLIENT === "ebg" ||
    process.env.REACT_APP_CLIENT === "ebg_stage";

  const getMandantById = () => {
    if (order.mandant === "ennstal" || order.mandant === "EBG" || order.mandant === "TEST_EBG") return 1;
    if (order.mandant === "ENW") return 2;
    if (order.mandant === "Gewog") return 4;
    if (order.mandant === "VKS") return 8;
    if (order.mandant === "Kärntner Friedenswerk") return 7;
    if (order.mandant === "wohnbaugruppe") return 11;

    return 1;
  };

  useEffect(() => {
    setMetaData(initialMetaData());
    setError("");
    setLoading(false);
    setFileUploaded(false);
  }, [show]);

  const upload = async () => {
    let err = "";
    if (!isTTAenabled) {
      const group = metaData.group || 0;

      if (invoicedata) {
        const duplicate = invoicedata.find((invoice) => invoice.re_nr === metaData.re_nr);
        if (duplicate) {
          err = "Fehler: Rechnungsnummer wurde schon verwendet";
        }
      }

      if (!moment(metaData.dok_eingangsdat).isValid()) {
        err = "Fehler: Datum muss ausgefüllt werden";
      }

      const year = moment(metaData.dok_eingangsdat).year();
      const actualYear = new Date().getFullYear() - 1;
      if (year < actualYear) {
        err = "Fehler: Aktuelles Jahr verwenden";
      }
      setError(err);

      if (err === "") {
        setLoading(true);
        try {
          const documentData = {
            ...metaData,
            group,
            umsatz: parseFloat(metaData.umsatz) || 0,
            steuer: parseFloat(metaData.steuer) || 0,
          };

          await dataStore.uploadInvoice(data.toJS()[0], documentData, orderId);
          setFileUploaded(true);
          hide(true, false);
        } catch (e) {
          console.error(e);
        } finally {
          setLoading(false);
        }
      }
    } else {
      try {
        setLoading(true);
        const { data: document } = await documentApi.uploadDokument(data.toJS()[0]);

        const mandantId = getMandantById();
        await auftragApi.uploadAuftragRechnung(mandantId, orderId, document);

        hide(true, false);
      } catch (e) {
        console.log(e);
        hide(false, true);
      } finally {
        setLoading(false);
        hide(false, false);
      }
    }
  };

  const hide = (uploaded = false, err = false) => {
    setMetaData(initialMetaData());
    setLoading(false);
    setError("");
    setFileUploaded(false);
    onHide(uploaded, err);
  };

  const handleChange = (e) => {
    setMetaData({ ...metaData, group: e.target.value });
  };

  const validated = false;

  return useObserver(() => (
    <Modal
      show={show}
      onHide={() => hide(false, false)}
      closeButton={false}
      closeOnBackdropClick={false}
      closeOnEscape={false}
      title='Rechnungsdetails'
      body={
        <LoadingSpinnerContainer loading={loading}>
          {() => (
            <Form validated={validated}>
              <Form.Group className={styles.invoiceText}>
                <p>
                  Bitte eventuelle Beilagen (zB Lieferscheine, Nachweise) mit der Rechnung gemeinsam in einer PDF-Datei
                  hochladen (Rechnung an erster Stelle)
                </p>
                {show && (
                  <Form.Label>
                    Rechnungs-Datei: <div className={styles.invoice}>{data[0].name}</div>
                  </Form.Label>
                )}
              </Form.Group>
              {!isTTAenabled && (
                <>
                  <Form.Group>
                    <Form.Label>Gruppe</Form.Label>
                    <Form.Control required as='select' onChange={handleChange} value={metaData.group}>
                      <option value={0}>Bitte Gruppe auswählen</option>
                      {rechnungsType &&
                        rechnungsType.map((d) => (
                          <option key={d.dokgr_bzg} value={d.dokgr_id}>
                            {d.dokgr_bzg}
                          </option>
                        ))}
                    </Form.Control>
                  </Form.Group>
                  {invoiceIsPdf && (
                    <div>
                      <Form.Group>
                        <Form.Label>Rechnungsnummer</Form.Label>
                        <Form.Control
                          as='input'
                          value={metaData.re_nr}
                          onChange={(e) => setMetaData({ ...metaData, re_nr: e.target.value })}
                          maxLength='35'
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Rechnungsdatum*</Form.Label>
                        <div>
                          <DatePicker
                            locale='de'
                            selected={metaData.dok_eingangsdat}
                            onChange={(date) => setMetaData({ ...metaData, dok_eingangsdat: date })}
                            dateFormat='d. MMMM yyyy'
                            className='form-control text-center'
                          />
                        </div>
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Umsatz (brutto)</Form.Label>
                        <Form.Control
                          as='input'
                          type='number'
                          value={metaData.umsatz}
                          onChange={(e) => setMetaData({ ...metaData, umsatz: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Steuerbetrag</Form.Label>
                        <Form.Control
                          as='input'
                          type='number'
                          value={metaData.steuer}
                          onChange={(e) => setMetaData({ ...metaData, steuer: e.target.value })}
                        />
                      </Form.Group>
                    </div>
                  )}
                </>
              )}
              {error !== "" && <p className='text-danger'>{error}</p>}
            </Form>
          )}
        </LoadingSpinnerContainer>
      }
      footer={
        <Button disabled={loading} variant='success' block onClick={upload}>
          Hochladen
        </Button>
      }
    />
  ));
};

UploadInvoiceModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  data: PropTypes.any.isRequired,
  invoicedata: PropTypes.any.isRequired,
  dataStore: PropTypes.any.isRequired,
  orderId: PropTypes.any.isRequired,
  rechnungsType: PropTypes.any.isRequired,
  invoiceIsPdf: PropTypes.bool.isRequired,
};

export default UploadInvoiceModal;
